/**
 * Assignee helpers for Todos people facet + column.
 * Separate from work_scope (Hop internal / Client project chips).
 * Bet: docs/product/bets/2026-08-17-org-wide-overdue-query-and-todos-assignee-facet.md
 */

export type AssigneeKind = 'hop' | 'client' | 'unknown';

export type AssigneeFacet = 'all' | 'me' | 'hop' | 'clients' | 'unassigned' | 'person';

export type MeIdentity = {
  id?: string | null;
  email?: string | null;
  name?: string | null;
};

/** Reports rows are assigned-to-you, so the most common assignee id is you. */
export function inferMeFromAssignments(
  rows: Array<{
    assignees?: BasecampPersonRef[];
    assignee?: { id?: string; name?: string; email?: string } | null;
  }>,
): MeIdentity {
  const counts = new Map<string, { id: string; email?: string; name?: string; n: number }>();
  for (const row of rows) {
    const people = row.assignees?.length
      ? row.assignees
      : row.assignee?.name
        ? [row.assignee]
        : [];
    const seen = new Set<string>();
    for (const p of people) {
      const id = p.id != null ? String(p.id) : '';
      const key = id || (p.email || '').toLowerCase() || (p.name || '').trim().toLowerCase();
      if (!key || seen.has(key)) continue;
      seen.add(key);
      const prev = counts.get(key) || { id, n: 0 };
      prev.n += 1;
      if (!prev.email && p.email) prev.email = p.email;
      if (!prev.name && p.name) prev.name = p.name;
      counts.set(key, prev);
    }
  }
  let best: { id: string; email?: string; name?: string; n: number } | null = null;
  for (const v of counts.values()) {
    if (!best || v.n > best.n) best = v;
  }
  if (!best) return {};
  return {
    id: best.id || null,
    email: best.email || null,
    name: best.name || null,
  };
}

export function mergeMeIdentity(...parts: MeIdentity[]): MeIdentity {
  const out: MeIdentity = {};
  for (const part of parts) {
    if (!out.id && part.id) out.id = String(part.id);
    if (!out.email && part.email) out.email = part.email;
    if (!out.name && part.name) out.name = part.name;
  }
  return out;
}

export interface BasecampPersonRef {
  id: string;
  name: string;
  email?: string;
  /** Basecamp person.client flag when present */
  client?: boolean;
}

const HOP_EMAIL_RE = /@hopstudios\.com$/i;

/** Classify a person from Basecamp payload (client flag, else email domain). */
export function classifyAssigneeKind(person: {
  email?: string | null;
  email_address?: string | null;
  client?: boolean | null;
} | null | undefined): AssigneeKind {
  if (!person) return 'unknown';
  if (person.client === true) return 'client';
  if (person.client === false) return 'hop';
  const email = String(person.email ?? person.email_address ?? '').trim();
  if (email && HOP_EMAIL_RE.test(email)) return 'hop';
  if (email) return 'client';
  return 'unknown';
}

export function normalizeAssigneesFromApi(raw: unknown): BasecampPersonRef[] {
  if (!Array.isArray(raw)) return [];
  const out: BasecampPersonRef[] = [];
  for (const p of raw) {
    if (!p || typeof p !== 'object') continue;
    const row = p as Record<string, unknown>;
    const id = row.id != null ? String(row.id) : '';
    const name = typeof row.name === 'string' ? row.name.trim() : '';
    if (!id && !name) continue;
    const emailRaw = row.email_address ?? row.email;
    const email = typeof emailRaw === 'string' && emailRaw.trim() ? emailRaw.trim() : undefined;
    const client = typeof row.client === 'boolean' ? row.client : undefined;
    out.push({ id: id || name, name: name || id, email, client });
  }
  return out;
}

/** Primary display assignee (first named person, else singular assignee). */
export function primaryAssigneeLabel(
  assignees: BasecampPersonRef[] | undefined,
  fallback?: { id?: string; name?: string } | null,
): string {
  const named = (assignees ?? []).map((a) => a.name).filter(Boolean);
  if (named.length) return named[0]!;
  const fb = (fallback?.name ?? '').trim();
  return fb || '';
}

export function formatAssigneesLabel(
  assignees: BasecampPersonRef[] | undefined,
  fallback?: { id?: string; name?: string } | null,
): string {
  const names = (assignees ?? []).map((a) => a.name).filter(Boolean);
  if (names.length === 0) {
    const fb = (fallback?.name ?? '').trim();
    return fb || '';
  }
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]}, ${names[1]}`;
  return `${names[0]} +${names.length - 1}`;
}

/** Row has at least one hop-classified assignee (or unknown names treated via kind on primary). */
export function rowMatchesAssigneeFacet(
  opts: {
    assignees?: BasecampPersonRef[];
    assignee?: { id?: string; name?: string } | null;
    facet: AssigneeFacet;
    personName?: string | null;
    meId?: string | null;
    meEmail?: string | null;
    meName?: string | null;
    /** When true, Me without identity is empty (Org cache). My reports omits this. */
    orgMode?: boolean;
  },
): boolean {
  const { facet, personName } = opts;
  const people = opts.assignees?.length
    ? opts.assignees
    : opts.assignee?.name
      ? [{ id: String(opts.assignee.id ?? ''), name: opts.assignee.name }]
      : [];

  if (facet === 'all') {
    return true;
  }
  if (facet === 'me') {
    const meId = opts.meId ? String(opts.meId) : '';
    const meEmail = opts.meEmail ? String(opts.meEmail).toLowerCase() : '';
    const meName = opts.meName ? String(opts.meName).trim().toLowerCase() : '';
    if (meId || meEmail || meName) {
      return people.some((p) => {
        const id = p.id != null ? String(p.id) : '';
        const email = (p.email || '').toLowerCase();
        const name = (p.name || '').trim().toLowerCase();
        return (
          (meId && id && id === meId) ||
          (meEmail && email && email === meEmail) ||
          (meName && name && name === meName)
        );
      });
    }
    // My reports is already assigned-to-me. Org without identity must not show everyone.
    return !opts.orgMode;
  }
  if (facet === 'unassigned') {
    return people.length === 0;
  }
  if (facet === 'person') {
    if (!personName) return true;
    return people.some((p) => p.name === personName);
  }
  if (facet === 'hop') {
    if (people.length === 0) return false;
    return people.some((p) => classifyAssigneeKind(p) === 'hop');
  }
  if (facet === 'clients') {
    if (people.length === 0) return false;
    return people.some((p) => classifyAssigneeKind(p) === 'client');
  }
  return true;
}

function collectAssigneePeople(
  rows: Array<{
    assignees?: BasecampPersonRef[];
    assignee?: { id?: string; name?: string; email?: string; client?: boolean } | null;
  }>,
): BasecampPersonRef[] {
  const people: BasecampPersonRef[] = [];
  for (const row of rows) {
    if (row.assignees?.length) {
      people.push(...row.assignees);
    } else if (row.assignee?.name) {
      people.push({
        id: String(row.assignee.id ?? ''),
        name: row.assignee.name,
        email: row.assignee.email,
        client: row.assignee.client,
      });
    }
  }
  return people;
}

export function uniqueAssigneeNames(
  rows: Array<{
    assignees?: BasecampPersonRef[];
    assignee?: { id?: string; name?: string; email?: string; client?: boolean } | null;
  }>,
  opts?: { kind?: AssigneeKind },
): string[] {
  const set = new Set<string>();
  for (const p of collectAssigneePeople(rows)) {
    if (!p.name) continue;
    if (opts?.kind && classifyAssigneeKind(p) !== opts.kind) continue;
    set.add(p.name);
  }
  return [...set].sort((a, b) => a.localeCompare(b));
}

export type GroupedAssigneeNames = {
  hop: string[];
  clients: string[];
  unknown: string[];
};

/** Names on the current report rows, split like Basecamp's Hop vs client picker. */
export function groupAssigneeNames(
  rows: Array<{
    assignees?: BasecampPersonRef[];
    assignee?: { id?: string; name?: string; email?: string; client?: boolean } | null;
  }>,
): GroupedAssigneeNames {
  const hop = new Set<string>();
  const clients = new Set<string>();
  const unknown = new Set<string>();
  for (const p of collectAssigneePeople(rows)) {
    if (!p.name) continue;
    const kind = classifyAssigneeKind(p);
    if (kind === 'hop') hop.add(p.name);
    else if (kind === 'client') clients.add(p.name);
    else unknown.add(p.name);
  }
  const byName = (a: string, b: string) => a.localeCompare(b);
  return {
    hop: [...hop].sort(byName),
    clients: [...clients].sort(byName),
    unknown: [...unknown].sort(byName),
  };
}
